import { Box, Button, Input } from '@mui/material'
import React, { useMemo } from 'react'
import { ILeafRequest, INode } from 'shared/types/tree'
import { NewAttachmentsProcessor } from './utilities'
import { bulkCreateLeavesFromList } from '../TreeActions'
import { Table } from 'shared/ui/Table/Table'
import { doInBatches } from 'shared/utils'
import { appendNodeToTree, NodeRequestParams } from '../TreeRequests'
import { GDriveFile } from 'infrastructure/googleDrive'
import { makeContentServicePostRequest } from 'infrastructure/contentService'
import {
  getFlowStatus,
  isRequestError,
  isPermanentError,
} from '../../contentService/utility'

export function Import({
  tree,
  accessToken,
}: {
  tree: INode
  accessToken: string
}) {
  const [folderId, setFolderId] = React.useState<string>()
  const [parsing, setParsing] = React.useState(false)
  const [parsingError, setParsingError] = React.useState<string>()
  const [loadingFolder, setLoadingFolder] = React.useState(false)
  const [loadingFolderStatus, setLoadingFolderStatus] = React.useState<
    { total: number; completed: number } | undefined
  >()
  const [processor, setProcessor] = React.useState<NewAttachmentsProcessor>()
  const [newAttachmentLeaves, setNewAttachmentLeaves] = React.useState<
    ILeafRequest[]
  >([])
  const [addingLeaves, setAddingLeaves] = React.useState(false)
  const [newAttachmentLists, setNewAttachmentLists] = React.useState<
    NodeRequestParams[]
  >([])
  const [addingLists, setAddingLists] = React.useState(false)
  const [skippedPdfs, setSkippedPdfs] = React.useState<GDriveFile[]>([])
  const [flowId, setFlowId] = React.useState<string>()
  const [contentServiceStatus, setContentServiceStatus] = React.useState<
    'idle' | 'adding' | 'added'
  >('idle')
  const [contentServiceProgress, setContentServiceProgress] =
    React.useState<string>()

  const intervalRef = React.useRef<NodeJS.Timeout | null>(null)

  React.useEffect(() => {
    if (!flowId) return
    const poll = async () => {
      try {
        const status = await getFlowStatus(flowId)
        if (isRequestError(status)) {
          setContentServiceProgress(`Error: ${status.errorText}`)
          if (isPermanentError(status) && intervalRef.current) {
            clearInterval(intervalRef.current)
          }
          return
        }
        if (status.data.completed) {
          setContentServiceStatus('added')
          setContentServiceProgress(undefined)
          if (intervalRef.current) clearInterval(intervalRef.current)
        } else {
          setContentServiceProgress('Running')
        }
      } catch (err) {
        setContentServiceProgress(`Error: ${err}`)
      }
    }

    intervalRef.current = setInterval(() => {
      poll()
    }, 1000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [flowId])

  React.useEffect(() => {
    if (!folderId) return
    setLoadingFolder(true)
    setParsingError(undefined)
    NewAttachmentsProcessor.create(accessToken, folderId, tree)
      .then((p) => {
        setProcessor(p)
        setLoadingFolder(false)
      })
      .catch((err) => {
        setParsingError(err.toString())
        setLoadingFolder(false)
      })
  }, [folderId])

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
      <Box>
        Add Content from Google Drive
        <Input
          placeholder="Google Drive Folder Id"
          value={folderId}
          style={{ margin: '0 1rem', width: '350px' }}
          onChange={(e) => setFolderId(e.target.value)}
        />
        {processor && !loadingFolder && (
          <Button
            disabled={parsing}
            onClick={() => {
              setParsing(true)
              processor
                .getNewAttachmentLeavesAndLists()
                .then((n) => {
                  setNewAttachmentLeaves(n.newAttachmentLeaves)
                  setNewAttachmentLists(n.newAttachmentLists)
                  setSkippedPdfs(n.skippedPdfs)
                  setParsing(false)
                })
                .catch((e) => {
                  setParsingError(e.toString())
                  setParsing(false)
                })
            }}
          >
            {parsing ? 'Parsing...' : 'Parse'}
          </Button>
        )}
      </Box>

      {(loadingFolder || loadingFolderStatus?.total) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <span>Loading Folder...</span>
          <progress
            style={{ flexGrow: '1' }}
            value={
              (loadingFolderStatus?.completed ?? 0) /
              (loadingFolderStatus?.total ?? 1)
            }
          />
          <span style={{ minWidth: '10rem', textAlign: 'right' }}>
            {`(${loadingFolderStatus?.completed ?? 0} / ${loadingFolderStatus?.total ?? 0})`}
          </span>
        </div>
      )}
      {parsingError && (
        <Box
          style={{
            background: 'oklch(80.8% 0.114 19.571)',
            padding: '0 8px',
          }}
        >
          <pre>{parsingError}</pre>
        </Box>
      )}
      <Box>Skipped Files: {skippedPdfs.length}</Box>
      <Table
        toolBarItems={[
          <Button
            disabled={addingLeaves}
            onClick={() => {
              setAddingLeaves(true)
              bulkCreateLeavesFromList({
                treeId: newAttachmentLeaves[0].tree_id!,
                versionId: newAttachmentLeaves[0].version!,
                leaves: newAttachmentLeaves,
              }).then(() => {
                setAddingLeaves(false)
              })
            }}
          >
            {addingLeaves ? 'Adding All...' : 'Add All'}
          </Button>,
        ]}
        title={'New Attachment Leaves'}
        items={newAttachmentLeaves}
        columns={useNewAttachmentLeavesColumns()}
      />
      <Table
        toolBarItems={[
          <Button
            disabled={addingLists}
            onClick={() => {
              setAddingLists(true)
              doInBatches(newAttachmentLists, appendNodeToTree).then(() => {
                setAddingLists(false)
              })
            }}
          >
            {addingLists ? 'Adding All...' : 'Add All'}
          </Button>,
        ]}
        title={'New Attachment Lists'}
        items={newAttachmentLists}
        columns={useNewAttachmentListsColumns()}
      />
      <Button
        disabled={
          (newAttachmentLeaves.length === 0 &&
            newAttachmentLists.length === 0) ||
          contentServiceStatus !== 'idle'
        }
        onClick={() => {
          setContentServiceStatus('adding')
          setContentServiceProgress('Running')
          makeContentServicePostRequest(
            'content/v1/bulk_add_content/job',
            {
              leaves: newAttachmentLeaves,
              lists: newAttachmentLists,
            },
            { google_auth_token: accessToken },
          )
            .then(async (response) => {
              if (!response.ok) {
                const text = await response.text()
                throw new Error(`Request failed: ${response.status} ${text}`)
              }
              const body = await response.json()
              const id = body?.data?.id
              if (id == null) {
                throw new Error('Response missing data.id')
              }
              setFlowId(id)
            })
            .catch((err) => {
              setContentServiceStatus('idle')
              setContentServiceProgress(`Error: ${err}`)
            })
        }}
      >
        {contentServiceStatus === 'added'
          ? 'Added via Content Service'
          : contentServiceStatus === 'adding'
            ? 'Adding via Content Service...'
            : 'Add via Content Service'}
      </Button>
      <Box>Status: {contentServiceProgress}</Box>
    </Box>
  )
}

function useNewAttachmentLeavesColumns() {
  return useMemo(() => {
    return [
      {
        title: 'Grandparent Name',
        width: 100,
        rowContent(row: ILeafRequest & { grandparentName: string }) {
          return row.grandparentName
        },
      },
      {
        title: 'Parent Name',
        width: 100,
        rowContent(row: ILeafRequest & { parentName: string }) {
          return row.parentName
        },
      },
      {
        title: 'Attachment Name',
        width: 100,
        rowContent(row: ILeafRequest) {
          return row.body.file.name
        },
      },
    ]
  }, [])
}

function useNewAttachmentListsColumns() {
  return useMemo(() => {
    return [
      {
        title: 'Parent Name',
        width: 100,
        rowContent(row: NodeRequestParams & { parentName: string }) {
          return row.parentName
        },
      },
      {
        title: 'Number of New Attachments',
        width: 10,
        rowContent(row: NodeRequestParams) {
          return row.leaves?.length
        },
      },
      {
        title: 'Attachments',
        width: 100,
        rowContent(row: NodeRequestParams) {
          return (
            <Box>
              {row.leaves?.map((leaf) => (
                <Box key={leaf.body?.file.id}>{leaf.body?.file.name}</Box>
              ))}
            </Box>
          )
        },
      },
    ]
  }, [])
}
